import { makeAutoObservable, runInAction } from 'mobx'

// evertel
import { decorate, injectable } from '@evertel/di'
import { isExtensionBanned, getFileExt, fileToUploadableMedia, getMediaType } from '@evertel/utils'
import { APIDataMediaToNormalizedMedia, NormalizedMedia, UploadableMedia } from '@evertel/media'
import { APIDataMedia } from '@evertel/types'

class DocumentMediaWriteWidgetController {
    loaded = false
    uploaded: Map<number, NormalizedMedia> = new Map()
    uploadQueue: Map<number, UploadableMedia> = new Map()
    deleteQueue: Set<number> = new Set()

    constructor(
    ) {
        makeAutoObservable(this)
    }

    init = (media: Array<APIDataMedia>) => {

        this.uploaded.clear()

        media.forEach((m) => {
            runInAction(() => {
                this.uploaded.set(
                    m.id,
                    APIDataMediaToNormalizedMedia(m)
                )
            })
        })

        runInAction(() => {
            this.loaded = true
        })
    }

    private isTypeAllowed = (file: File, typesAllowed?: string[]): boolean => {
        if (!typesAllowed?.length) return true

        const [category] = file.type.split('/')

        return typesAllowed.some(t =>
            (t === 'images' && category === 'image') ||
            (t === 'videos' && category === 'video') ||
            (t === 'audio' && category === 'audio') ||
            ((t === 'files' || t === 'application') && (category === 'application' || category === 'text'))
        )
    }

    addToUploadQueue = async (files: File[], typesAllowed?: string[]) => {
        const rejectedExtensions = new Set()
        const rejectedTypes = new Set()

        for (const file of files) {
            const ext = getFileExt(file)

            if (isExtensionBanned(ext)) {
                rejectedExtensions.add(ext)
                continue
            }

            if (!this.isTypeAllowed(file, typesAllowed)) {
                const [category] = file.type.split('/')
                rejectedTypes.add(category)
                continue
            }

            const uploadableMedia = await fileToUploadableMedia(file)
            runInAction(() => {
                this.uploadQueue.set(uploadableMedia.id, uploadableMedia)
            })
        }

        if (rejectedExtensions.size) {
            throw new Error(`Unsupported file format: ${[...rejectedExtensions].join(', ')}`)
        }
        if (rejectedTypes.size) {
            throw new Error(`File type not allowed`)
        }
    }

    deleteFromUploadQueue = (mediaId: number): void => {
        this.uploadQueue.delete(
            mediaId
        )
    }

    deleteUploaded = (mediaId: number): void => {
        // remove media from uploaded array
        this.uploaded.delete(mediaId)

        // add mediaId to the list of media to delete on save
        this.deleteQueue.add(
            mediaId
        )
    }

    get mediaArrays() {
        return {
            uploaded: [...this.uploaded.values()],
            uploadQueue: [...this.uploadQueue.values()],
            deleteQueue: [...this.deleteQueue.values()]
        }
    }
}

decorate(injectable(), DocumentMediaWriteWidgetController)

export { DocumentMediaWriteWidgetController }
